/**
 * Phone number formatting utility
 * Handles international phone number formatting with country code detection
 */

export interface CountryCodePattern {
  code: string;
  length: number;
  country?: string;
  iso?: string;
}

/**
 * Comprehensive list of country codes sorted by length (longest first)
 * to ensure accurate matching of country codes
 */
export const COUNTRY_CODE_PATTERNS: CountryCodePattern[] = [
  // 4-digit country codes (special services)
  { code: '1684', length: 4, country: 'American Samoa', iso: 'AS' },
  { code: '1671', length: 4, country: 'Guam', iso: 'GU' },
  { code: '1670', length: 4, country: 'Northern Mariana Islands', iso: 'MP' },
  { code: '1787', length: 4, country: 'Puerto Rico', iso: 'PR' },
  { code: '1939', length: 4, country: 'Puerto Rico' },
  { code: '1340', length: 4, country: 'US Virgin Islands', iso: 'VI' },
  { code: '1242', length: 4, country: 'Bahamas', iso: 'BS' },
  { code: '1246', length: 4, country: 'Barbados', iso: 'BB' },
  { code: '1264', length: 4, country: 'Anguilla', iso: 'AI' },
  { code: '1268', length: 4, country: 'Antigua and Barbuda', iso: 'AG' },
  { code: '1284', length: 4, country: 'British Virgin Islands', iso: 'VG' },
  { code: '1345', length: 4, country: 'Cayman Islands', iso: 'KY' },
  { code: '1441', length: 4, country: 'Bermuda', iso: 'BM' },
  { code: '1473', length: 4, country: 'Grenada', iso: 'GD' },
  { code: '1649', length: 4, country: 'Turks and Caicos', iso: 'TC' },
  { code: '1664', length: 4, country: 'Montserrat', iso: 'MS' },
  { code: '1721', length: 4, country: 'Sint Maarten', iso: 'SX' },
  { code: '1758', length: 4, country: 'Saint Lucia', iso: 'LC' },
  { code: '1767', length: 4, country: 'Dominica', iso: 'DM' },
  { code: '1784', length: 4, country: 'Saint Vincent and the Grenadines', iso: 'VC' },
  { code: '1809', length: 4, country: 'Dominican Republic', iso: 'DO' },
  { code: '1829', length: 4, country: 'Dominican Republic' },
  { code: '1849', length: 4, country: 'Dominican Republic' },
  { code: '1868', length: 4, country: 'Trinidad and Tobago', iso: 'TT' },
  { code: '1869', length: 4, country: 'Saint Kitts and Nevis', iso: 'KN' },
  { code: '1876', length: 4, country: 'Jamaica', iso: 'JM' },
  // 3-digit country codes
  { code: '211', length: 3, country: 'South Sudan', iso: 'SS' },
  { code: '212', length: 3, country: 'Morocco', iso: 'MA' },
  { code: '213', length: 3, country: 'Algeria', iso: 'DZ' },
  { code: '216', length: 3, country: 'Tunisia', iso: 'TN' },
  { code: '218', length: 3, country: 'Libya', iso: 'LY' },
  { code: '220', length: 3, country: 'Gambia', iso: 'GM' },
  { code: '221', length: 3, country: 'Senegal', iso: 'SN' },
  { code: '222', length: 3, country: 'Mauritania', iso: 'MR' },
  { code: '223', length: 3, country: 'Mali', iso: 'ML' },
  { code: '224', length: 3, country: 'Guinea', iso: 'GN' },
  { code: '225', length: 3, country: 'Ivory Coast', iso: 'CI' },
  { code: '226', length: 3, country: 'Burkina Faso', iso: 'BF' },
  { code: '227', length: 3, country: 'Niger', iso: 'NE' },
  { code: '228', length: 3, country: 'Togo', iso: 'TG' },
  { code: '229', length: 3, country: 'Benin', iso: 'BJ' },
  { code: '230', length: 3, country: 'Mauritius', iso: 'MU' },
  { code: '231', length: 3, country: 'Liberia', iso: 'LR' },
  { code: '232', length: 3, country: 'Sierra Leone', iso: 'SL' },
  { code: '233', length: 3, country: 'Ghana', iso: 'GH' },
  { code: '234', length: 3, country: 'Nigeria', iso: 'NG' },
  { code: '235', length: 3, country: 'Chad', iso: 'TD' },
  { code: '236', length: 3, country: 'Central African Republic', iso: 'CF' },
  { code: '237', length: 3, country: 'Cameroon', iso: 'CM' },
  { code: '238', length: 3, country: 'Cape Verde', iso: 'CV' },
  { code: '239', length: 3, country: 'São Tomé and Príncipe', iso: 'ST' },
  { code: '240', length: 3, country: 'Equatorial Guinea', iso: 'GQ' },
  { code: '241', length: 3, country: 'Gabon', iso: 'GA' },
  { code: '242', length: 3, country: 'Republic of the Congo', iso: 'CG' },
  { code: '243', length: 3, country: 'Democratic Republic of the Congo', iso: 'CD' },
  { code: '244', length: 3, country: 'Angola', iso: 'AO' },
  { code: '245', length: 3, country: 'Guinea-Bissau', iso: 'GW' },
  { code: '246', length: 3, country: 'British Indian Ocean Territory', iso: 'IO' },
  { code: '247', length: 3, country: 'Ascension Island', iso: 'AC' },
  { code: '248', length: 3, country: 'Seychelles', iso: 'SC' },
  { code: '249', length: 3, country: 'Sudan', iso: 'SD' },
  { code: '250', length: 3, country: 'Rwanda', iso: 'RW' },
  { code: '251', length: 3, country: 'Ethiopia', iso: 'ET' },
  { code: '252', length: 3, country: 'Somalia', iso: 'SO' },
  { code: '253', length: 3, country: 'Djibouti', iso: 'DJ' },
  { code: '254', length: 3, country: 'Kenya', iso: 'KE' },
  { code: '255', length: 3, country: 'Tanzania', iso: 'TZ' },
  { code: '256', length: 3, country: 'Uganda', iso: 'UG' },
  { code: '257', length: 3, country: 'Burundi', iso: 'BI' },
  { code: '258', length: 3, country: 'Mozambique', iso: 'MZ' },
  { code: '260', length: 3, country: 'Zambia', iso: 'ZM' },
  { code: '261', length: 3, country: 'Madagascar', iso: 'MG' },
  { code: '262', length: 3, country: 'Réunion', iso: 'RE' },
  { code: '263', length: 3, country: 'Zimbabwe', iso: 'ZW' },
  { code: '264', length: 3, country: 'Namibia', iso: 'NA' },
  { code: '265', length: 3, country: 'Malawi', iso: 'MW' },
  { code: '266', length: 3, country: 'Lesotho', iso: 'LS' },
  { code: '267', length: 3, country: 'Botswana', iso: 'BW' },
  { code: '268', length: 3, country: 'Eswatini', iso: 'SZ' },
  { code: '269', length: 3, country: 'Comoros', iso: 'KM' },
  { code: '290', length: 3, country: 'Saint Helena', iso: 'SH' },
  { code: '291', length: 3, country: 'Eritrea', iso: 'ER' },
  { code: '297', length: 3, country: 'Aruba', iso: 'AW' },
  { code: '298', length: 3, country: 'Faroe Islands', iso: 'FO' },
  { code: '299', length: 3, country: 'Greenland', iso: 'GL' },
  { code: '350', length: 3, country: 'Gibraltar', iso: 'GI' },
  { code: '351', length: 3, country: 'Portugal', iso: 'PT' },
  { code: '352', length: 3, country: 'Luxembourg', iso: 'LU' },
  { code: '353', length: 3, country: 'Ireland', iso: 'IE' },
  { code: '354', length: 3, country: 'Iceland', iso: 'IS' },
  { code: '355', length: 3, country: 'Albania', iso: 'AL' },
  { code: '356', length: 3, country: 'Malta', iso: 'MT' },
  { code: '357', length: 3, country: 'Cyprus', iso: 'CY' },
  { code: '358', length: 3, country: 'Finland', iso: 'FI' },
  { code: '359', length: 3, country: 'Bulgaria', iso: 'BG' },
  { code: '370', length: 3, country: 'Lithuania', iso: 'LT' },
  { code: '371', length: 3, country: 'Latvia', iso: 'LV' },
  { code: '372', length: 3, country: 'Estonia', iso: 'EE' },
  { code: '373', length: 3, country: 'Moldova', iso: 'MD' },
  { code: '374', length: 3, country: 'Armenia', iso: 'AM' },
  { code: '375', length: 3, country: 'Belarus', iso: 'BY' },
  { code: '376', length: 3, country: 'Andorra', iso: 'AD' },
  { code: '377', length: 3, country: 'Monaco', iso: 'MC' },
  { code: '378', length: 3, country: 'San Marino', iso: 'SM' },
  { code: '379', length: 3, country: 'Vatican', iso: 'VA' },
  { code: '380', length: 3, country: 'Ukraine', iso: 'UA' },
  { code: '381', length: 3, country: 'Serbia', iso: 'RS' },
  { code: '382', length: 3, country: 'Montenegro', iso: 'ME' },
  { code: '383', length: 3, country: 'Kosovo', iso: 'XK' },
  { code: '385', length: 3, country: 'Croatia', iso: 'HR' },
  { code: '386', length: 3, country: 'Slovenia', iso: 'SI' },
  { code: '387', length: 3, country: 'Bosnia and Herzegovina', iso: 'BA' },
  { code: '389', length: 3, country: 'North Macedonia', iso: 'MK' },
  { code: '420', length: 3, country: 'Czech Republic', iso: 'CZ' },
  { code: '421', length: 3, country: 'Slovakia', iso: 'SK' },
  { code: '423', length: 3, country: 'Liechtenstein', iso: 'LI' },
  { code: '500', length: 3, country: 'Falkland Islands', iso: 'FK' },
  { code: '501', length: 3, country: 'Belize', iso: 'BZ' },
  { code: '502', length: 3, country: 'Guatemala', iso: 'GT' },
  { code: '503', length: 3, country: 'El Salvador', iso: 'SV' },
  { code: '504', length: 3, country: 'Honduras', iso: 'HN' },
  { code: '505', length: 3, country: 'Nicaragua', iso: 'NI' },
  { code: '506', length: 3, country: 'Costa Rica', iso: 'CR' },
  { code: '507', length: 3, country: 'Panama', iso: 'PA' },
  { code: '508', length: 3, country: 'Saint Pierre and Miquelon', iso: 'PM' },
  { code: '509', length: 3, country: 'Haiti', iso: 'HT' },
  { code: '590', length: 3, country: 'Guadeloupe', iso: 'GP' },
  { code: '591', length: 3, country: 'Bolivia', iso: 'BO' },
  { code: '592', length: 3, country: 'Guyana', iso: 'GY' },
  { code: '593', length: 3, country: 'Ecuador', iso: 'EC' },
  { code: '594', length: 3, country: 'French Guiana', iso: 'GF' },
  { code: '595', length: 3, country: 'Paraguay', iso: 'PY' },
  { code: '596', length: 3, country: 'Martinique', iso: 'MQ' },
  { code: '597', length: 3, country: 'Suriname', iso: 'SR' },
  { code: '598', length: 3, country: 'Uruguay', iso: 'UY' },
  { code: '599', length: 3, country: 'Curaçao', iso: 'CW' },
  { code: '670', length: 3, country: 'East Timor', iso: 'TL' },
  { code: '672', length: 3, country: 'Antarctica', iso: 'AQ' },
  { code: '673', length: 3, country: 'Brunei', iso: 'BN' },
  { code: '674', length: 3, country: 'Nauru', iso: 'NR' },
  { code: '675', length: 3, country: 'Papua New Guinea', iso: 'PG' },
  { code: '676', length: 3, country: 'Tonga', iso: 'TO' },
  { code: '677', length: 3, country: 'Solomon Islands', iso: 'SB' },
  { code: '678', length: 3, country: 'Vanuatu', iso: 'VU' },
  { code: '679', length: 3, country: 'Fiji', iso: 'FJ' },
  { code: '680', length: 3, country: 'Palau', iso: 'PW' },
  { code: '681', length: 3, country: 'Wallis and Futuna', iso: 'WF' },
  { code: '682', length: 3, country: 'Cook Islands', iso: 'CK' },
  { code: '683', length: 3, country: 'Niue', iso: 'NU' },
  { code: '685', length: 3, country: 'Samoa', iso: 'WS' },
  { code: '686', length: 3, country: 'Kiribati', iso: 'KI' },
  { code: '687', length: 3, country: 'New Caledonia', iso: 'NC' },
  { code: '688', length: 3, country: 'Tuvalu', iso: 'TV' },
  { code: '689', length: 3, country: 'French Polynesia', iso: 'PF' },
  { code: '690', length: 3, country: 'Tokelau', iso: 'TK' },
  { code: '691', length: 3, country: 'Micronesia', iso: 'FM' },
  { code: '692', length: 3, country: 'Marshall Islands', iso: 'MH' },
  { code: '850', length: 3, country: 'North Korea', iso: 'KP' },
  { code: '852', length: 3, country: 'Hong Kong', iso: 'HK' },
  { code: '853', length: 3, country: 'Macau', iso: 'MO' },
  { code: '855', length: 3, country: 'Cambodia', iso: 'KH' },
  { code: '856', length: 3, country: 'Laos', iso: 'LA' },
  { code: '880', length: 3, country: 'Bangladesh', iso: 'BD' },
  { code: '886', length: 3, country: 'Taiwan', iso: 'TW' },
  { code: '960', length: 3, country: 'Maldives', iso: 'MV' },
  { code: '961', length: 3, country: 'Lebanon', iso: 'LB' },
  { code: '962', length: 3, country: 'Jordan', iso: 'JO' },
  { code: '963', length: 3, country: 'Syria', iso: 'SY' },
  { code: '964', length: 3, country: 'Iraq', iso: 'IQ' },
  { code: '965', length: 3, country: 'Kuwait', iso: 'KW' },
  { code: '966', length: 3, country: 'Saudi Arabia', iso: 'SA' },
  { code: '967', length: 3, country: 'Yemen', iso: 'YE' },
  { code: '968', length: 3, country: 'Oman', iso: 'OM' },
  { code: '970', length: 3, country: 'Palestine', iso: 'PS' },
  { code: '971', length: 3, country: 'United Arab Emirates', iso: 'AE' },
  { code: '972', length: 3, country: 'Israel', iso: 'IL' },
  { code: '973', length: 3, country: 'Bahrain', iso: 'BH' },
  { code: '974', length: 3, country: 'Qatar', iso: 'QA' },
  { code: '975', length: 3, country: 'Bhutan', iso: 'BT' },
  { code: '976', length: 3, country: 'Mongolia', iso: 'MN' },
  { code: '977', length: 3, country: 'Nepal', iso: 'NP' },
  { code: '992', length: 3, country: 'Tajikistan', iso: 'TJ' },
  { code: '993', length: 3, country: 'Turkmenistan', iso: 'TM' },
  { code: '994', length: 3, country: 'Azerbaijan', iso: 'AZ' },
  { code: '995', length: 3, country: 'Georgia', iso: 'GE' },
  { code: '996', length: 3, country: 'Kyrgyzstan', iso: 'KG' },
  { code: '998', length: 3, country: 'Uzbekistan', iso: 'UZ' },
  // 2-digit country codes (most common)
  { code: '20', length: 2, country: 'Egypt', iso: 'EG' },
  { code: '27', length: 2, country: 'South Africa', iso: 'ZA' },
  { code: '30', length: 2, country: 'Greece', iso: 'GR' },
  { code: '31', length: 2, country: 'Netherlands', iso: 'NL' },
  { code: '32', length: 2, country: 'Belgium', iso: 'BE' },
  { code: '33', length: 2, country: 'France', iso: 'FR' },
  { code: '34', length: 2, country: 'Spain', iso: 'ES' },
  { code: '36', length: 2, country: 'Hungary', iso: 'HU' },
  { code: '39', length: 2, country: 'Italy', iso: 'IT' },
  { code: '40', length: 2, country: 'Romania', iso: 'RO' },
  { code: '41', length: 2, country: 'Switzerland', iso: 'CH' },
  { code: '43', length: 2, country: 'Austria', iso: 'AT' },
  { code: '44', length: 2, country: 'United Kingdom', iso: 'GB' },
  { code: '45', length: 2, country: 'Denmark', iso: 'DK' },
  { code: '46', length: 2, country: 'Sweden', iso: 'SE' },
  { code: '47', length: 2, country: 'Norway', iso: 'NO' },
  { code: '48', length: 2, country: 'Poland', iso: 'PL' },
  { code: '49', length: 2, country: 'Germany', iso: 'DE' },
  { code: '51', length: 2, country: 'Peru', iso: 'PE' },
  { code: '52', length: 2, country: 'Mexico', iso: 'MX' },
  { code: '53', length: 2, country: 'Cuba', iso: 'CU' },
  { code: '54', length: 2, country: 'Argentina', iso: 'AR' },
  { code: '55', length: 2, country: 'Brazil', iso: 'BR' },
  { code: '56', length: 2, country: 'Chile', iso: 'CL' },
  { code: '57', length: 2, country: 'Colombia', iso: 'CO' },
  { code: '58', length: 2, country: 'Venezuela', iso: 'VE' },
  { code: '60', length: 2, country: 'Malaysia', iso: 'MY' },
  { code: '61', length: 2, country: 'Australia', iso: 'AU' },
  { code: '62', length: 2, country: 'Indonesia', iso: 'ID' },
  { code: '63', length: 2, country: 'Philippines', iso: 'PH' },
  { code: '64', length: 2, country: 'New Zealand', iso: 'NZ' },
  { code: '65', length: 2, country: 'Singapore', iso: 'SG' },
  { code: '66', length: 2, country: 'Thailand', iso: 'TH' },
  { code: '77', length: 2, country: 'Kazakhstan', iso: 'KZ' },
  { code: '81', length: 2, country: 'Japan', iso: 'JP' },
  { code: '82', length: 2, country: 'South Korea', iso: 'KR' },
  { code: '84', length: 2, country: 'Vietnam', iso: 'VN' },
  { code: '86', length: 2, country: 'China', iso: 'CN' },
  { code: '90', length: 2, country: 'Turkey', iso: 'TR' },
  { code: '91', length: 2, country: 'India', iso: 'IN' },
  { code: '92', length: 2, country: 'Pakistan', iso: 'PK' },
  { code: '93', length: 2, country: 'Afghanistan', iso: 'AF' },
  { code: '94', length: 2, country: 'Sri Lanka', iso: 'LK' },
  { code: '95', length: 2, country: 'Myanmar', iso: 'MM' },
  { code: '98', length: 2, country: 'Iran', iso: 'IR' },
  // 1-digit country codes
  { code: '1', length: 1, country: 'US/Canada/Caribbean', iso: 'US' },
  { code: '7', length: 1, country: 'Russia/Kazakhstan', iso: 'RU' },
];

/**
 * Formats a phone number with international country code detection
 * @param phoneNumber - The phone number to format (can include non-digit characters)
 * @returns Formatted phone number string in the format: +XX (XXX) XXX-XXXX
 * @example
 * formatPhoneNumber('84966036626') // Returns: '+84 (966) 036-6626'
 * formatPhoneNumber('15559876543') // Returns: '+1 (555) 987-6543'
 * formatPhoneNumber('40123456789') // Returns: '+40 (123) 456-789'
 */
export function formatPhoneNumber(phoneNumber: string | undefined): string {
  if (!phoneNumber) {
    return '';
  }

  // Remove all non-digit characters
  const cleaned = phoneNumber.replace(/\D/g, '');

  // Check if it's a valid phone number length
  if (cleaned.length < 10) {
    return phoneNumber; // Return original if too short
  }

  // Detect country code based on patterns
  let countryCode = '';
  let nationalNumber = '';

  // Try to match country code
  for (const pattern of COUNTRY_CODE_PATTERNS) {
    if (cleaned.startsWith(pattern.code)) {
      countryCode = pattern.code;
      nationalNumber = cleaned.substring(pattern.length);
      break;
    }
  }

  // If no country code detected, treat as US number without country code
  if (!countryCode) {
    nationalNumber = cleaned;
  }

  // Format based on national number length
  if (nationalNumber.length === 10) {
    // Standard 10-digit format: (XXX) XXX-XXXX
    const areaCode = nationalNumber.substring(0, 3);
    const firstPart = nationalNumber.substring(3, 6);
    const secondPart = nationalNumber.substring(6, 10);

    if (countryCode) {
      return `+${countryCode} (${areaCode}) ${firstPart}-${secondPart}`;
    } else {
      return `(${areaCode}) ${firstPart}-${secondPart}`;
    }
  } else if (nationalNumber.length === 9) {
    // 9-digit format: (XXX) XXX-XXX
    const areaCode = nationalNumber.substring(0, 3);
    const firstPart = nationalNumber.substring(3, 6);
    const secondPart = nationalNumber.substring(6, 9);

    if (countryCode) {
      return `+${countryCode} (${areaCode}) ${firstPart}-${secondPart}`;
    } else {
      return `(${areaCode}) ${firstPart}-${secondPart}`;
    }
  } else if (nationalNumber.length === 8) {
    // 8-digit format: XXXX-XXXX
    const firstPart = nationalNumber.substring(0, 4);
    const secondPart = nationalNumber.substring(4, 8);

    if (countryCode) {
      return `+${countryCode} ${firstPart}-${secondPart}`;
    } else {
      return `${firstPart}-${secondPart}`;
    }
  } else {
    // For other lengths, just format with dashes every 3 digits
    const formatted = nationalNumber.match(/.{1,3}/g)?.join('-') || nationalNumber;

    if (countryCode) {
      return `+${countryCode} ${formatted}`;
    } else {
      return formatted;
    }
  }
}
